import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import './ProductsPages.css';
import ProductsPmsHeader from '../../components/ProductsPmsHeader';
import TitleAndContentCard from '../../components/TitleAndContentCard';
import ExpandableCard from '../../components/ExpandableCard';
import CheckCircle from '../../icons/CheckCircle';
import WhyChooseOurPms from '../../components/WhyChooseOurPms';
import { useTranslation } from 'react-i18next';
import useLanguagePrefix from '../../LanguagePrefix';

function PropertyManagementSystem() {
    const { t } = useTranslation();
    const langPrefix = useLanguagePrefix();
    const features = [
        { title: t('All-in-One Solution'), content: t('Seamlessly manage reservations, housekeeping, and reporting from a single, intuitive platform.') },
        { title: t('Automated Workflows'), content: t('Reduce manual tasks and save time with intelligent automation.') },
        { title: t('Integrated Booking Engine'), content: t('Convert direct bookings with a high-performing, commission-free booking system.') },
        { title: t('Robust Reporting & Insights'), content: t('Access real-time data and analytics to make informed decisions.') },
    ];

    const roomPlanningExpandableCards = [
        { title: t("Plan & Visualize"), content: t("Easily map out your entire facility with an intuitive interface that allows you to organize rooms, define spaces, and optimize occupancy. Our user-friendly dashboard provides a structured and functional view of your property.") },
        { title: t("Configure"), content: t("Personalize your room categories, pricing structures, and availability settings to match your property's current state and needs. Our system’s flexible configurations allow you to streamline room assignments, prevent overbookings, and enhance guest satisfaction.") },
        { title: t("Seamless Synchronization"), content: t("Get seamless updates from all booking channels with automatic synchronization between your property’s calendar, OTA, and native booking engines. Maintain accuracy, optimize occupancy rates & revenue, and prevent discrepancies.") },
    ];

    const guestManagementExpandableCards = [
        {
            title: t('Personalized Guest Profiles.'), content: (
                <>
                    <p className="mb-3">
                        {t('Every guest is unique, and so should their experience. Prostay equips you with the necessary tools to tailor each stay to individual preferences, ensuring guest satisfaction.')}
                    </p>
                    <p className="mb-3">
                        {t('Store and manage essential details like contact information, stay history, preferred room types, amenities, and add-ons, making every interaction more personalized.')}
                    </p>
                    <p className="mb-3">
                        {t('You also have the versatility of grouping your guests with the “Guest Status” feature. With this tool, you can create a VIP group, or a disallowed group.')}
                    </p>
                    <p className="mb-3">
                        {t('For more advanced hoteliers, we’ve designed a module tagged “Guest Custom Fields”. It lets you create new fields for guest profiles. This gives room for flexibility, and the unmatched creativity needed to max-out Prostay’s features.')}
                    </p>
                </>
            )
        },
        {
            title: t('Effortlessly Manage Check-in, Stay, and Check-out.'), content: (
                <>
                    <p className="mb-3">
                        {t('Easily view and manage guest information directly from the calendar page, including check-in and check-out dates at a glance.')}
                    </p>
                    <p className="mb-3">
                        {t(`Need to make changes? Prostay gives you total control over guest details and stay parameters. You can quickly extend guests' stay, honour early departure requests, process last-minute room changes, and more in a few clicks.`)}
                    </p>
                    <p className="mb-3">
                        {t("Prostay gives you total control and versatility through  features like amenities and add-ons. These features help you further improve guest satisfaction through personalized service delivery.")}
                    </p>
                </>
            )
        },
        {
            title: t('Smart Billing and Payment Processing.'), content: (
                <>
                    <p className="mb-3">
                        {t('Prostay simplifies payment processing, making every transaction fast and secure. Our system supports various payment methods, including debit and credit cards, PayPal, and more.')}
                    </p>
                    <p className="mb-3">
                        {t(`For on-site purchases and service payments, Prostay integrates seamlessly with Tableview, our dedicated Point of Sale (POS) platform, ensuring all transactions are processed securely and effortlessly, right from the front desk or any service point.`)}
                    </p>
                </>
            )
        },
        { title: t('Insightful Analytics for detecting trends.'), content: t('Understanding how well your property is performing starts with the right insights. That’s why Prostay includes dedicated tools. These tools can be used to ascertain the number of bookings, turnaways, and more. These insights can help you make better decisions that will positively affect your property guest satisfaction rate.') },
    ];

    const sourceManagementExpandableCards = [
        {
            title: t('Track sources with precision.'), content: (
                <>
                    <p className='mb-3'>
                        {t('Keeping proper records of which agent refers guests to your property is essential for both operational clarity and accountability. It allows you to easily identify top-performing agents, spot inactive ones, and maintain transparency in your partnerships.')}
                    </p>
                    <p>
                        {t('This not only supports fair commission handling but also helps you uphold agreements and avoid potential disputes.')}
                    </p>
                </>
            )
        },
        {
            title: t('Create custom profiles for each agent/source'), content: (
                <>
                    <p className='mb-3'>
                        {t('With Prostay, creating customized profiles for each agent or source type takes just a few simple steps.')}
                    </p>
                    <p>
                        {t('You can also add clear descriptions to each source type, making it easy for your staff to quickly understand what each one represents at a glance.')}
                    </p>
                </>
            )
        },
        { title: t('Track/manage commissions easily'), content: t(`Set custom commission rates for each source, and let Prostay handle the tracking and calculations. You'll always have a clear record of amounts due, paid, and outstanding, avoiding the need for guesswork and minimizing accounting errors.`) }];

    const [selectedTabIndex, setSelectedTabIndex] = useState(0);
    const [activeIndex, setActiveIndex] = useState(0);

    const handleActiveIndex = (index) => {
        setActiveIndex(index);
    }

    return (
        <div>
            <Helmet>
                <title>Prostay – Hotel Property Management System (PMS)</title>
                <meta name="description" content="Powerful PMS software for hotels and resorts. Handle reservations, housekeeping, guest services, and reporting—cloud or on-premise." />
                  <link rel="canonical" href={"https://www.prostay.com/products/property-management-system"} /> 

                  <meta property="og:title" content="Prostay – Hotel Property Management System (PMS)" />
                <meta property="og:description" content="Powerful PMS software for hotels and resorts. Handle reservations, housekeeping, guest services, and reporting—cloud or on-premise." />
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://www.prostay.com/products/property-management-system" />
                <meta property="og:image" content='https://prostay.com/images/ogImage.png' />

                <meta property="og:site_name" content="Prostay" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Prostay – Hotel Property Management System (PMS)" />
                <meta name="twitter:description" content="Powerful PMS software for hotels and resorts. Handle reservations, housekeeping, guest services, and reporting—cloud or on-premise." />
                <meta name="twitter:image" content='https://prostay.com/images/ogImage.png' />
                <meta name="twitter:site" content="@prostaypms" />
                <meta name="twitter:creator" content="@prostaypms" />


            </Helmet>
            <div className='product-page'>
                <ProductsPmsHeader />
                <div className='d-flex justify-content-center product-page-content'>
                    <div className='page-content'>
                        <div className='d-flex justify-content-between py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                            <div className='col-12 col-md-5'>
                                <h2>{t('See our PMS Platform Custom Features')}</h2>
                            </div>
                            <div className='col-12 col-md-5'>
                                <p className='body-large'>
                                    {t('Prostay PMS is not only cloud-based, it also runs on-premise with secure native apps for Windows, macOS, Android, and iPad — and it works offline, syncing automatically when back online.')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className='d-flex features-carousel-wrapper'>
                    <div className='d-flex gap-3 features-carousel pb-5'>
                        {
                            features.map((feature, index) =>
                                <TitleAndContentCard title={feature.title} content={feature.content} width={384} height={178} onClick={() => handleActiveIndex(index)} />
                            )
                        }
                    </div>
                    {/* <div className='d-flex gap-2 justify-content-center'>
                    {
                        features.map((_, index) => 
                            <div 
                                className={`dot ${index === activeIndex ? 'active': ''}`}
                                style={{width: index === activeIndex ? '32px' : '6px',
                                        background: index === activeIndex ? '#1643E0' : '#808698',
                                        borderRadius: '32px',
                                        height: '6px',
                                        transition: 'width 0.3s ease-in-out',
                                     }}>
                             </div> ) 
                    }
                </div> */}
                </div>
                <div className='d-flex justify-content-center py-2 py-md-5' style={{ flexWrap: 'wrap' }}>
                    <div className='page-content d-flex justify-content-between gap-4'>
                        <div className='col-12 col-md-4'>
                            <div className={selectedTabIndex == 0 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(0)}>
                                <label className='label-medium'> {t('Room Planning')} </label>
                                {
                                    selectedTabIndex == 0 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 1 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(1)}>
                                <label className='label-medium'> {t('Reservations Management')} </label>
                                {
                                    selectedTabIndex == 1 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 2 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(2)}>
                                <label className='label-medium'> {t('Guest Management')} </label>
                                {
                                    selectedTabIndex == 2 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 3 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(3)}>
                                <label className='label-medium'> {t('Agent/Sources Management')} </label>
                                {
                                    selectedTabIndex == 3 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 4 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(4)}>
                                <label className='label-medium'> {t('Housekeeping')} </label>
                                {
                                    selectedTabIndex == 4 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                            <div className={selectedTabIndex == 5 ? 'tab-btn focused' : 'tab-btn'} onClick={() => setSelectedTabIndex(5)}>
                                <label className='label-medium'> {t('Reports, Statistics & Analytics')} </label>
                                {
                                    selectedTabIndex == 5 &&
                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14 5L21 12M21 12L14 19M21 12H3" stroke="#1643E0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                }
                            </div>
                        </div>
                        <div className='col tab-content'>
                            {
                                selectedTabIndex == 0 &&
                                <>
                                    <h4>{t('Room Planning')}</h4>
                                    <img src='/images/pms-room-planning.png' alt={"Room planning"} />
                                    <p>
                                        {t('Visualize and effortlessly manage your property with Prostay’s property management system. Our intuitive system lets you map your facility, optimize operations, and seamlessly integrate accommodation types with real-time calendar and reservation booking updates, ensuring accuracy and efficiency at every phase.')}
                                    </p>
                                    <p>
                                        {t('Prostay’s PMS gives you complete control over room availability and pricing, allowing you to stand out with amenities, promo discounts, and more. You can always get creative on Prostay through customizable fields.')}
                                    </p>
                                    <p>
                                        {t('You can also effortlessly manage housekeeping operations, streamline daily workflows, and ensure seamless synchronization with OTAs and native booking engines, keeping your facility running smoothly and efficiently around the clock.')}
                                    </p>
                                    <h5 className='my-4'>{t('Smart Room Planning')}</h5>
                                    {
                                        roomPlanningExpandableCards.map(cardContent =>
                                            <ExpandableCard title={cardContent.title} content={cardContent.content} />
                                        )
                                    }
                                    <h5 className='my-4'>{t('Plan Effectively')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Digitize your entire property’s space.')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Monitor reservations & room status through one module.')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Improve bookings through OTA booking channels.')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Optimize revenue & view comprehensive analytics.')}</p>
                                    </div>
                                    <p className='mt-4'>
                                        {t('Prostay is designed for both efficiency and results, seamlessly managing every aspect of room planning, bookings, and revenue collection.')}
                                    </p>
                                    <p>
                                        {t('Take control of your property’s key performance indicators - achieve peak performance with Prostay.')}
                                    </p>
                                </>
                            }
                            {
                                selectedTabIndex == 1 &&
                                <>
                                    <h4>{t('Reservations Management')}</h4>
                                    <img src='/images/reservationmanagement.png' alt={"Room planning"} />
                                    <p>
                                        {t('Prostay’s Reservations Management module is the heart of your front office operations. Designed for simplicity and scalability, it gives your team full visibility into current, upcoming, and past bookings — whether made via your Booking Engine, Channel Manager, or directly by your staff.')}
                                    </p>
                                    <p>
                                        {t('Modify, cancel, or create reservations with ease. View guest profiles, payment details, room allocations, and special requests all in one place.')}
                                    </p>
                                    <p>
                                        {t('Automated syncing with the Channel Manager and Booking Engine ensures availability and rates are always accurate across all platforms.')}
                                    </p>
                                    <p>
                                        {t('Integrated seamlessly with the other Prostay modules such as Prostay Nexus, Housekeeping or Tableview, you are always in control with up to date reservations information.')}
                                    </p>
                                    <h5 className='my-4'>{t('Frictionless Booking Experience')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Quick search, filter, and booking tools')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Flexible rate plans, discounts & packages')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Smart conflict alerts and overbooking prevention')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Clean, calendar-based reservation view')}</p>
                                    </div>
                                    <p className='mt-4'>
                                        {t('Prostay is designed to redefine the reservation experience, eliminating the inefficiencies of traditional property management systems (PMS).')}
                                    </p>
                                    <p>
                                        {t('Elevate your presence in the global hospitality industry and adopt our easily integrable reservation solution.')}
                                    </p>
                                </>
                            }
                            {
                                selectedTabIndex == 2 &&
                                <>
                                    <h4>{t('Guest Management')}</h4>
                                    <img src='/images/guestmanagement.png' alt={"Room planning"} />
                                    <p>
                                        {t('Creating a great guest experience starts long before check-in and extends beyond departure. With Prostay’s Guest Management System, you have the tools to deliver personalized service, enhance guest satisfaction, and build long-term loyalty.')}
                                    </p>
                                    <p>
                                        {t('We’ve engineered the Prostay experience to seamlessly streamline guest management, making it hassle-free through the following unique selling points.')}
                                    </p>
                                    {
                                        guestManagementExpandableCards.map(cardContent =>
                                            <ExpandableCard title={cardContent.title} content={cardContent.content} />
                                        )
                                    }
                                    <h5 className='my-4'>{t('Level Up Guest Satisfaction with Prostay')}</h5>
                                    <p className='mt-4'>
                                        {t(`Delivering exceptional guest experiences doesn't have to be complicated. With Prostay, you get the tools to understand, engage, and manage guests.`)}
                                    </p>
                                    <p>
                                        {t(`Let's help you make satisfaction your new standard.`)}
                                    </p>
                                </>
                            }
                            {
                                selectedTabIndex == 3 &&
                                <>
                                    <h4>{t('Agent/Sources Management')}</h4>
                                    <img src='/images/agent.png' alt={"Room planning"} />
                                    <p>
                                        {t('Managing multiple booking agents can quickly become complex without the right system. Prostay simplifies this process by giving you the tools to track, organize, and analyze every individual or group contributing to your bookings.')}
                                    </p>
                                    <p>
                                        {t('We make this possible by allowing you to define agent profiles & source types and set the rules or policies that guide how they work with your property.')}
                                    </p>
                                    <h5 className='my-4'>{t('Why Prostay Is the Smarter Way to Manage Agents/Sources')}</h5>
                                    {
                                        sourceManagementExpandableCards.map(cardContent =>
                                            <ExpandableCard title={cardContent.title} content={cardContent.content} />
                                        )
                                    }
                                    <h5 className='my-4'>{t('Key Benefits?')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Smarter decision making with reports')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Reduced administrative workload')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Scalable structure')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Transparent commission tracking')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Enhance revenue forecasting')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Prevent commission-related disputes')}</p>
                                    </div>
                                    <p className='mt-3'>
                                        {t('With Prostay, say goodbye to managing agents and source types manually. Embrace a more streamlined, data-driven, enabling you build more robust, functional, and long-lasting partnerships.')}
                                    </p>
                                </>
                            }
                            {
                                selectedTabIndex == 4 &&
                                <>
                                    <h4>{t('Housekeeping')}</h4>
                                    <img src='/images/housekeeping.png' alt={"Room planning"} />
                                    <p>
                                        {t('Prostay’s Housekeeping module is built to give your property full visibility and control over daily room operations. With real-time synchronization to the front office, you’ll always know which rooms are clean, occupied, in need of inspection, or ready for new arrivals.')}
                                    </p>
                                    <p>
                                        {t('Easily assign tasks to housekeeping staff, track progress, and approve room readiness from any device. The module supports mobile updates from staff on the go, allowing for faster turnarounds and clearer communication. Whether managing a small hotel or a full-scale resort, Prostay keeps your operations organized and guests satisfied.')}
                                    </p>
                                    <h5 className='my-4'>{t('Seamlessly manage room status, cleaning schedules, and team efficiency.')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Live room status updates')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Smart task assignment and scheduling')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Support for multiple housekeeping teams')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Queue Rush housekeeping management')}</p>
                                    </div>
                                </>
                            }
                            {
                                selectedTabIndex == 5 &&
                                <>
                                    <h4>{t('Reports')}</h4>
                                    <img src='/images/analytics.png' alt={"Room planning"} />
                                    <p>
                                        {t('Prostay’s Reports module delivers clear, real-time insights into your property’s performance—giving you the tools to track, analyze, and act with confidence. From daily operations to long-term forecasting, every report is built to help you understand your business inside and out.')}
                                    </p>
                                    <p>
                                        {t('Easily access pre-built operational, financial, and guest reports or customize your own to suit your unique needs. Whether you’re analyzing occupancy trends, revenue streams, or staff performance, Prostay ensures your data is always accurate, up-to-date, and accessible.')}
                                    </p>
                                    <h5 className='my-4'>{t('Insightful data to drive smarter decisions.')}</h5>
                                    <div className='d-flex align-items-center gap-2'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Real-time performance dashboards')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Occupancy, revenue, and ADR analysis')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Customizable filters & export options')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Forecasting tools for strategic planning')}</p>
                                    </div>
                                    <div className='d-flex align-items-center gap-2 mt-3'>
                                        <CheckCircle />
                                        <p style={{ margin: 0, fontWeight: 600 }}>{t('Integration with accounting & POS modules')}</p>
                                    </div>
                                    <p className='mt-4'>
                                        {t('Make informed decisions with a reporting engine that works as fast and as smart as your team.')}
                                    </p>
                                </>
                            }
                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center mt-5 d-none d-md-flex'>
                    <div className='page-content d-flex w-100 justify-content-center align-items-center' style={{ textAlign: "center" }}>
                        <div className='col-lg-6 d-flex flex-column align-items-center'>
                            <h2 style={{ textAlign: "center", maxWidth: '582px' }}>{t('Revolutionize your business with our PMS Platform')}</h2>
                            <p className='body-large' style={{ textAlign: "center", maxWidth: '582px' }}>
                                {t('Run your property smarter with a fully integrated PMS — on-premise, cloud, and mobile-ready with seamless access to bookings, housekeeping, reports, and guest communication.')}
                            </p>

                        </div>
                    </div>
                </div>
                <div className='d-flex justify-content-center bottom-padding'>
                    <div className='page-content '>
                        <div className='white-card p-3 p-md-5 d-flex justify-content-between mt-3 mt-lg-5 gap-4 gap-lg-1' style={{ flexWrap: "wrap" }}>
                            <div className='col-12 col-lg-5'>
                                <h4>{t('Experience Prostay in Action')}</h4>
                                <p className='mt-4'>
                                    {t('See how Prostay transforms the way your property operates — from bookings and housekeeping to guest messaging and financials. ')}
                                </p>
                                <p>
                                    {t('Whether you run a hotel, resort, or vacation rental, our fully integrated PMS adapts to your workflow, on any device, online or offline.')}
                                </p>
                                <a href={` /request-demo`} style={{ textDecoration: 'none' }}><button className='light-blue-filled-btn-small mt-5'>
                                    {t('Request Demo')}
                                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M13 5.99992H1Z" fill="white" />
                                        <path d="M8.33333 1.33325L13 5.99992M13 5.99992L8.33333 10.6666M13 5.99992H1" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button></a>
                            </div>
                            <div className='col-12 col-lg-6'>
                                <img src='/images/experience-in-action.png' style={{ maxWidth: "100%" }} alt={"Room planning"} />
                            </div>
                        </div>
                    </div>
                </div>
                <WhyChooseOurPms
                    headerTitle="Take Control of your Business Today!"
                    headerDescription="Cutting-edge Property Management System designed to revolutionize the way hotels, and resorts from boutique to large-scale, operate and serve their guests."
                    sectionTitle="Why Choose Our PMS?"
                    sectionDescription="Explore our most frequently asked questions about our Property Management System—from setup and migration to daily operations. Designed to help you feel confident every step of the way."
                    cards={[
                        { title: "What types of properties can use Prostay?", description: "Prostay PMS is designed to support a wide range of property types, including hotels, resorts, hostels, serviced apartments, and vacation rentals. Its flexible architecture allows full customization based on your operational needs." },
                        { title: "Does Prostay support both cloud and on-premise deployment?", description: "Yes. Prostay is a hybrid platform that works both in the cloud and on-premise. You can install it on Windows, Mac, Android tablets, and iPads, ensuring full functionality even in remote locations or low-connectivity environments." },
                        { title: "Does the PMS support multiple properties or chains?", description: "Absolutely. Prostay is built to scale, with full multi-property support, ideal for hotel groups, resort chains, or vacation rental portfolios. Manage everything from a single dashboard." },
                        { title: "Can Prostay integrate with other tools we already use?", description: "Absolutely. Prostay offers an open API and seamless integrations with third-party tools like payment gateways, access control systems, accounting software, and more — making it easy to connect with your existing tech stack." },
                        { title: "Will you help us migrate from our current PMS to Prostay?", description: "Yes, absolutely. Our team offers free of charge migration from any other Property Management System you’re currently using. We handle the entire process—from data transfer to configuration—ensuring a smooth and hassle-free transition with zero downtime for your operations." }

                    ]}
                />
            </div>
        </div>
    );
}

export default PropertyManagementSystem;
